import { BigIntMath, Fraction, Percent, isAddress } from "@eulerxyz/core-sdk"
import { createSelector } from "@reduxjs/toolkit"
import type { UseQueryResult } from "@tanstack/react-query"
import { type Address, getAddress, maxUint256, zeroAddress } from "viem"
import { GetVaultReturnType } from "../actions/getVault"
import { GetGenericVaultData } from "../hooks"
import type { GetManagedVaultData, GetVaultData } from "../query"
import { selectQueryStatus } from "../utils"
import { selectCurrentLiquidationLTV } from "./accountInfo"
import { selectPrice, selectUnitOfAccountWithBackup } from "./pricing"
import { CollateralValueInfo } from "./simulation"
export const NON_BORROWABLE = "NON_BORROWABLE"
export const LTV_DEFAULT_CALCULATION_SCALE = 10000n
const UINT256_MAX = BigInt(2n ** 256n - 1n)

export type NativeApy = {
  apy: number
  source?: string
  claimUrl?: string
  apyDuration?: string
}

export const selectBorrowInterestRateAPY = createSelector(
  [(vault: GetVaultData) => vault?.irmInfo?.interestRateInfo[0]?.borrowAPY],
  (borrowInterestRateAPY) => {
    if (!borrowInterestRateAPY)
      return { formatted: "0", raw: new Percent("0", 10n ** 27n) }
    const percent = new Percent(borrowInterestRateAPY || "0", 10n ** 27n)

    return {
      formatted: percent.toPercentageString(3),
      raw: percent,
    }
  },
)

export const selectSupplyInterestRateAPY = createSelector(
  [
    (vault: GetVaultData) =>
      vault?.irmInfo.interestRateInfo[0]?.supplyAPY ?? 0n,
  ],
  (supplyInterestRateAPY) => {
    return new Percent(
      supplyInterestRateAPY || "0",
      10n ** 27n,
    ).toPercentageString(2)
  },
)

export const selectFeesToClaim = createSelector(
  [
    (vault: GetVaultData) => vault,
    (_, allUnitOfAccountPrices: Map<Address, bigint>) => allUnitOfAccountPrices,
  ],
  (vault, allUnitOfAccountPrices) => {
    if (!vault) return null

    const feeShare = vault.protocolFeeShare
    const accumulatedFees = vault.accumulatedFeesAssets

    // Skip vaults with no accumulated fees
    if (!accumulatedFees || accumulatedFees === 0n) return null

    // Calculate claimable fees based on protocol fee share
    // feeShare is in basis points (e.g. 5000n = 50%)
    // Using pure bigint arithmetic with base of 10000n
    const claimableFees = (accumulatedFees * feeShare) / 10000n

    const claimableFeesAsFraction = new Fraction(
      claimableFees,
      10n ** BigInt(vault.assetDecimals),
    )
    const unitOfAccountWithBackup = selectUnitOfAccountWithBackup(vault)
    const claimableFeesAsAmount = selectPrice(
      claimableFees,
      vault,
      allUnitOfAccountPrices.get(unitOfAccountWithBackup) ?? 0n,
    )

    return {
      claimableFeesAsAmount,
      claimableFeesAsFraction,
    }
  },
)

export const selectFeesConverted = createSelector(
  [
    (vault: GetVaultData) => vault,
    (_, allUnitOfAccountPrices: Map<Address, bigint>) => allUnitOfAccountPrices,
    (_, __, assets: bigint) => assets,
  ],
  (vault, allUnitOfAccountPrices, assets) => {
    if (!vault) return null

    const convertedAsFraction = new Fraction(
      assets,
      10n ** BigInt(vault.assetDecimals),
    )
    const unitOfAccountWithBackup = selectUnitOfAccountWithBackup(vault)
    const convertedAsAmount = selectPrice(
      assets,
      vault,
      allUnitOfAccountPrices.get(unitOfAccountWithBackup) ?? 0n,
    )

    return {
      convertedAsAmount,
      convertedAsFraction,
    }
  },
)

export const selectFeesTotal = createSelector(
  [
    (vault: GetVaultData) => vault,
    (_, allUnitOfAccountPrices: Map<Address, bigint>) => allUnitOfAccountPrices,
    (_, __, assets: bigint) => assets,
  ],
  (vault, allUnitOfAccountPrices, assets) => {
    if (!vault) return null

    const feeShare = vault.protocolFeeShare
    const accumulatedFees = vault.accumulatedFeesAssets

    // Skip vaults with no accumulated fees
    if (!accumulatedFees || accumulatedFees === 0n) return null

    const totalFees = (accumulatedFees * feeShare) / 10000n + BigInt(assets)

    const totalAsFraction = new Fraction(
      totalFees,
      10n ** BigInt(vault.assetDecimals),
    )
    const unitOfAccountWithBackup = selectUnitOfAccountWithBackup(vault)
    const totalAsAmount = selectPrice(
      totalFees,
      vault,
      allUnitOfAccountPrices.get(unitOfAccountWithBackup) ?? 0n,
    )

    return {
      totalAsAmount,
      totalAsFraction,
    }
  },
)

export enum ApyType {
  Supply = "supply",
  Borrow = "borrow",
}

export type ApyParams = {
  baseApy?: number
  doesNotAutoCompound?: boolean
  isNonBorrowable?: boolean
  nativeApy?: NativeApy
  rewardsApy?: number
  utilization?: number | string
}

export const selectNetAPY = createSelector(
  (apys: ApyParams) => apys,
  (_: ApyParams, type: ApyType) => type,
  (
    {
      baseApy = 0,
      doesNotAutoCompound = false,
      isNonBorrowable,
      nativeApy = { apy: 0 },
      rewardsApy = 0,
      utilization = 0,
    },
    type,
  ) => {
    const compoundingMultiplier = 1 + baseApy / 100

    // FE-2928: For non-auto-compounding vaults, scale native APY by utilization
    const utilizationScalar = doesNotAutoCompound
      ? 1 - Number(utilization) / 100
      : 1
    const scaledNativeApy = nativeApy.apy * utilizationScalar

    if (type === ApyType.Supply) {
      return baseApy + compoundingMultiplier * scaledNativeApy + rewardsApy
    }

    // FE-2928: For borrow APY, invert native APY if vault is borrowable and doesn't auto-compound
    const borrowNativeApy =
      doesNotAutoCompound && !isNonBorrowable ? -nativeApy.apy : nativeApy.apy
    return baseApy + compoundingMultiplier * borrowNativeApy - rewardsApy
  },
)

export const selectVaultCollateralPriceInfo = (vault: GetVaultData) =>
  vault?.collateralPriceInfo

export const selectVaultCollateralLTVInfo = (vault: GetVaultData) =>
  vault?.collateralLTVInfo

export const selectCollateralLtvInfoRaw = createSelector(
  [selectVaultCollateralLTVInfo, (_, collateral: Address) => collateral],
  (ltvInfos, collateral) => {
    return ltvInfos?.find(
      (ltv) => getAddress(ltv.collateral) === getAddress(collateral),
    )
  },
)

export const selectCollateralLtv = createSelector(
  [selectVaultCollateralLTVInfo, (_, collateral: Address) => collateral],
  (ltvInfos, collateral) => {
    const ltvInfo = ltvInfos?.find(
      (ltv) => getAddress(ltv.collateral) === getAddress(collateral),
    )

    if (!ltvInfo) return
    const currentTimestamp = BigInt(Math.floor(Date.now() / 1000))
    const isRampingDown = ltvInfo.targetTimestamp > currentTimestamp
    const currentLiquidationLTV = selectCurrentLiquidationLTV(ltvInfo)

    return {
      borrowLtv: new Percent(ltvInfo.borrowLTV, 10_000).toPercentageString(2),
      liquidationLtv: new Percent(
        ltvInfo.liquidationLTV,
        10_000,
      ).toPercentageString(2),
      borrowLtvRaw: ltvInfo.borrowLTV,
      liquidationLtvRaw: ltvInfo.liquidationLTV,
      initialLiquidationLTV: ltvInfo.initialLiquidationLTV,
      initialLiquidationLTVPercent: new Percent(
        ltvInfo.initialLiquidationLTV,
        10_000,
      ).toPercentageString(2),
      currentLiquidationLTV: currentLiquidationLTV,
      currentLiquidationLTVPercent: new Percent(
        currentLiquidationLTV,
        10_000,
      ).toPercentageString(2),
      targetLiquidationLTV: ltvInfo.liquidationLTV,
      targetLiquidationLTVPercent: new Percent(
        ltvInfo.liquidationLTV,
        10_000,
      ).toPercentageString(2),
      rampBeginning: ltvInfo.targetTimestamp - ltvInfo.rampDuration,
      rampDuration: ltvInfo.rampDuration,
      rampDurationRemaining: ltvInfo.targetTimestamp - currentTimestamp,
      isRampingDown,
    }
  },
)

export const selectTotalShares = (vault: GetVaultData) => vault?.totalShares
export const selectTotalBorrowed = (vault: GetVaultData) => vault?.totalBorrowed
export const selectSupplyCap = (vault: GetVaultData) => vault?.supplyCap
export const selectBorrowCap = (vault: GetVaultData) => vault?.borrowCap

export const selectTotalAssets = (vault: GetGenericVaultData) =>
  vault?.totalAssets
export const selectTotalCash = (vault: GetVaultData) => vault?.totalCash

export const selectVaultDecimals = (vault: GetGenericVaultData) =>
  vault?.vaultDecimals

export const selectAvailableToBorrow = createSelector(
  [selectTotalCash, selectTotalBorrowed, selectBorrowCap, selectVaultDecimals],
  (_totalCash, _totalBorrowed, _borrowCap) => {
    const totalCash = _totalCash || 0n
    const totalBorrowed = _totalBorrowed || 0n
    const borrowCap = _borrowCap === UINT256_MAX ? null : _borrowCap || 0n

    if (!borrowCap) return totalCash

    return BigIntMath.min(
      totalCash,
      borrowCap > totalBorrowed ? borrowCap - totalBorrowed : 0n,
    )
  },
)

export const selectAsset = createSelector(
  [(vault?: GetGenericVaultData) => vault],
  (raw) =>
    (raw &&
      ({
        address: raw.asset,
        decimals: Number(raw.assetDecimals?.toString() || 18n),
        symbol: raw.assetSymbol,
        name: raw.assetName,
        meta: raw.assetMeta,
      } as const)) ||
    undefined,
)

export const selectManagedAsset = createSelector(
  [(vault?: GetManagedVaultData) => vault],
  (raw) =>
    raw &&
    ({
      address: raw.asset,
      decimals: Number(raw.assetDecimals),
      name: raw.assetName,
      symbol: raw.assetSymbol,
    } as const),
)

export const selectVaultWithAsset = createSelector(
  [(vault?: GetVaultData) => vault],
  (raw) =>
    raw &&
    ({
      address: raw.asset,
      decimals: Number(raw.assetDecimals),
      name: raw.assetName,
      symbol: raw.assetSymbol,
      vault: raw.vault,
      vaultSymbol: raw.vaultSymbol,
    } as const),
)

export const selectAvailableToBorrowWithAsset = createSelector(
  [selectAvailableToBorrow, selectAsset, selectVaultDecimals],
  (availableToBorrow_, asset, vaultDecimals = 18n) => {
    const availableToBorrow = new Fraction(availableToBorrow_ || 0n)
    return {
      availableToBorrow: availableToBorrow.divide(10n ** vaultDecimals),
      asset,
    }
  },
)

export const selectTotalAssetsWithAsset = createSelector(
  [selectTotalAssets, selectAsset, selectVaultDecimals],
  (totalAssets_, asset, vaultDecimals = 18n) => {
    const totalAssets = new Fraction(totalAssets_ || 0n)
    return {
      totalAssets: totalAssets.divide(10n ** vaultDecimals),
      asset,
    }
  },
)

export const selectTotalCashWithAsset = createSelector(
  [selectTotalCash, selectAsset, selectVaultDecimals],
  (totalCash_, asset, vaultDecimals = 18n) => {
    const totalCash = new Fraction(totalCash_ || 0n)
    return {
      totalCash: totalCash.divide(10n ** vaultDecimals),
      asset,
    }
  },
)

export const selectTotalBorrowedWithAsset = createSelector(
  [selectTotalBorrowed, selectAsset, selectVaultDecimals],
  (totalBorrowed_, asset, vaultDecimals = 18n) => {
    const totalBorrowed = new Fraction(totalBorrowed_ || 0n)
    return {
      totalBorrowed: totalBorrowed.divide(10n ** vaultDecimals),
      asset,
    }
  },
)

export const selectSupplyCapWithAsset = createSelector(
  [selectSupplyCap, selectAsset, selectVaultDecimals],
  (supplyCap_, asset, vaultDecimals = 18n) => {
    if (supplyCap_ === UINT256_MAX) return { supplyCap: null, asset: null }

    const supplyCap = new Fraction(supplyCap_ || 0n)
    return {
      supplyCap: supplyCap.divide(10n ** vaultDecimals),
      asset,
    }
  },
)

export const selectBorrowCapWithAsset = createSelector(
  [selectBorrowCap, selectAsset, selectVaultDecimals],
  (borrowCap_, asset, vaultDecimals = 18n) => {
    if (borrowCap_ === UINT256_MAX) return { borrowCap: null, asset: null }

    const borrowCap = new Fraction(borrowCap_ || 0n)
    return {
      borrowCap: borrowCap.divide(10n ** vaultDecimals),
      asset,
    }
  },
)

export const selectUtilization = createSelector(
  [selectTotalAssets, selectTotalBorrowed],
  (_totalAssets, _totalBorrow) => {
    if (!_totalAssets || !_totalBorrow) return "0"

    const totalAssets = new Fraction(_totalAssets)
    const totalBorrow = new Fraction(_totalBorrow)

    return totalBorrow.greaterThan(0) && totalAssets.greaterThan(0)
      ? new Fraction(100).multiply(totalBorrow.divide(totalAssets)).toFixed(2)
      : "0"
  },
)

export const selectCapacity = createSelector(
  [(capacity?: bigint) => capacity, (_, total?: bigint) => total],
  (capacity, total) => {
    if (!capacity || !total || capacity === maxUint256 || capacity === 0n) {
      if (capacity !== 0n && capacity !== maxUint256) {
        return {
          percentage: 0,
          percentageFormatted: "0",
        }
      }
      return {
        percentage: 0,
        percentageFormatted: "-",
      }
    }
    const scale = 10n ** 2n
    const fraction = (total * scale * 100n) / capacity
    const percentage = parseFloat(`${fraction / scale}.${fraction % scale}`)
    const percentageFormatted = percentage.toFixed(2)

    return {
      percentage,
      percentageFormatted,
    }
  },
)

export const selectCollateralAddresses = createSelector(
  [selectVaultCollateralLTVInfo],
  (collateralLTVInfo) => {
    return (
      collateralLTVInfo
        ?.filter(({ borrowLTV }) => borrowLTV > 0n)
        .map(({ collateral }) => collateral) || []
    )
  },
)

export const selectCollateralLTVInfo = createSelector(
  [
    selectVaultCollateralLTVInfo,
    (_, collateralAddress?: Address) => collateralAddress,
  ],
  (collateralLTVInfo, collateralAddress) => {
    return (
      collateralLTVInfo?.find(
        (info) => info.collateral === collateralAddress,
      ) || null
    )
  },
)

export const selectCollateralPriceInfo = createSelector(
  [
    selectVaultCollateralPriceInfo,
    (_, collateralAddress?: Address) => collateralAddress,
  ],
  (collateralPriceInfo, collateralAddress) => {
    return (
      collateralPriceInfo?.find(
        (info) =>
          collateralAddress &&
          getAddress(info.asset) === getAddress(collateralAddress),
      ) || null
    )
  },
)

export const selectCollateralAmountInAmountOutPure = createSelector(
  [selectCollateralPriceInfo],
  (collateralPriceInfo) => {
    if (!collateralPriceInfo?.amountIn || !collateralPriceInfo?.amountOutMid) {
      return {
        amountIn: 1n,
        amountOut: 1n,
      }
    }

    return {
      amountIn: BigInt(collateralPriceInfo.amountIn),
      amountOut: BigInt(collateralPriceInfo.amountOutMid),
    }
  },
)

export const selectBorrowingLTVBigInt = createSelector(
  [selectCollateralLTVInfo],
  (collateralLTVInfo) => collateralLTVInfo?.borrowLTV || 0n,
)

export const selectMaxLeverageFromLTVScaledBigInt = createSelector(
  [selectBorrowingLTVBigInt],
  (borrowingLTV) => {
    const scale = LTV_DEFAULT_CALCULATION_SCALE
    const base = 10_000n
    const result = (scale * base) / (base - borrowingLTV)
    return { result, scale, base }
  },
)

export const selectMaxLeverageForTwoDifferentAssetsBigInt = createSelector(
  [
    (data, vaultAAddress, _) => selectBorrowingLTVBigInt(data, vaultAAddress),
    (data, _, vaultBAddress) => selectBorrowingLTVBigInt(data, vaultBAddress),
  ],
  (vaultALTV, vaultBLTV) => {
    const scale = LTV_DEFAULT_CALCULATION_SCALE
    const base = 10_000n
    const one = base
    const quotient = one * BigInt(vaultALTV)
    const denominator = one - BigInt(vaultBLTV)
    const result = quotient / denominator - 200n + one

    return {
      result,
      scale,
      base,
    }
  },
)

export const selectMaxLeverageForTwoDifferentAssets = createSelector(
  [
    (data, vaultAAddress, vaultBAddress) =>
      selectMaxLeverageForTwoDifferentAssetsBigInt(
        data,
        vaultAAddress,
        vaultBAddress,
      ),
  ],
  (rawResult) => {
    return {
      raw: rawResult,
      formatted: `${(
        Number(rawResult.result) / Number(rawResult.scale)
      ).toFixed(2)}`,
    }
  },
)

export interface MaxLeverageGeneralizedBigIntParams {
  longVaultAddress?: Address
  totalCollateralValueRaw?: CollateralValueInfo
  totalCollateralValueBorrowing?: CollateralValueInfo
}

export interface MaxLeverageGeneralizedBigIntResult {
  result: number
  isReady: boolean
  ltvNumber?: number
}

export const selectMaxLeverageGeneralized = createSelector<
  [
    (data: GetVaultData, params: MaxLeverageGeneralizedBigIntParams) => bigint,
    (
      data: GetVaultData,
      params: MaxLeverageGeneralizedBigIntParams,
    ) => MaxLeverageGeneralizedBigIntParams,
  ],
  MaxLeverageGeneralizedBigIntResult
>(
  [
    (
      data: GetVaultData,
      { longVaultAddress }: MaxLeverageGeneralizedBigIntParams,
    ) => selectBorrowingLTVBigInt(data, longVaultAddress),
    (_, passedData) => passedData,
  ],
  (
    longVaultLTVAgainstShortVaultBigInt, // we need the long asset LTV
    passedData: MaxLeverageGeneralizedBigIntParams,
  ): MaxLeverageGeneralizedBigIntResult => {
    const { totalCollateralValueRaw, totalCollateralValueBorrowing } =
      passedData
    const ltvNumber = Number(longVaultLTVAgainstShortVaultBigInt) / 10_000

    if (
      totalCollateralValueRaw?.number === undefined ||
      totalCollateralValueBorrowing?.number === undefined ||
      totalCollateralValueRaw?.number === null ||
      totalCollateralValueBorrowing?.number === null
    ) {
      return {
        result: 0,
        isReady: false,
        ltvNumber,
      }
    }
    const multiplier = 1 / (1 - ltvNumber)

    const maxLeverage =
      1 +
      (totalCollateralValueBorrowing?.number /
        totalCollateralValueRaw?.number) *
        multiplier
    if (isNaN(maxLeverage) || maxLeverage === Infinity) {
      console.error(
        `[Error] maxLeverage is ${maxLeverage}  totalCollateralValueBorrowing: ${totalCollateralValueBorrowing?.number} totalCollateralValueRaw: ${totalCollateralValueRaw?.number} multiplier: ${multiplier}`,
      )
      return {
        result: 0,
        isReady: false,
        ltvNumber,
      }
    }

    return {
      result: maxLeverage * 0.995, // safety margin
      isReady: true,
      ltvNumber,
    }
  },
)

export const selectLeverageModalData = createSelector(
  [
    selectCollateralAddresses,
    selectCollateralAmountInAmountOutPure,
    (data: GetVaultData, _) => data,
  ],
  (collateralAddresses, collateralAmountInAmountOutPrice, allVaultData) => {
    return {
      collateralAddresses,
      collateralAmountInAmountOutPrice,
      allVaultData,
    }
  },
)

export interface SelectVaultsResult {
  vault: Address
  collateralVaults?: Address[]
  asset: Address
  assetSymbol: string
  borrowApy?: bigint
  collateralLTVInfo?: NonNullable<GetVaultData>["collateralLTVInfo"]
  utilisation: number
}

export const selectVault = createSelector(
  [(vault: GetVaultData) => vault],
  (vault): SelectVaultsResult => {
    if (!vault) throw Error("Vault data is missing")
    return {
      vault: vault.vault,
      collateralVaults: vault.collateralLTVInfo
        ?.filter(({ borrowLTV }) => borrowLTV > 0n)
        .map(({ collateral }) => collateral),
      asset: vault.asset,
      assetSymbol: vault.assetSymbol,
      borrowApy: vault.irmInfo.interestRateInfo[0]?.borrowAPY,
      collateralLTVInfo: vault.collateralLTVInfo,
      utilisation: +selectUtilization(vault),
    }
  },
)

export const selectProvider = (vault: GetVaultData) => vault?.oracleInfo.name

export const selectLiquidationPenalty = createSelector(
  [(vault: GetVaultData) => vault?.maxLiquidationDiscount],
  (maxLiquidationDiscount) => {
    if (!maxLiquidationDiscount) return "0"

    return Number(BigInt(maxLiquidationDiscount)) / 100
  },
)

export const selectOracleInfos = createSelector(
  [
    (results: UseQueryResult<GetVaultData>[]) => selectQueryStatus(results),
    (results: UseQueryResult<GetVaultData>[]) => results,
  ],
  (status, results) => {
    const data: NonNullable<GetVaultData>["oracleInfo"][] = []
    if (status.isSuccess) {
      for (const result of results) {
        if (
          result.data &&
          isAddress(result.data.oracleInfo?.oracle) &&
          result.data.oracleInfo?.oracle !== zeroAddress
        ) {
          data.push(result.data.oracleInfo)
        }
      }
    } else {
      return { ...status, data: undefined }
    }

    return { ...status, data }
  },
)

export const selectInterestRateModel = (vault: GetVaultData) =>
  vault?.irmInfo.interestRateModel

export const selectVaultFee = createSelector(
  [(vault: GetVaultData) => vault?.interestFee],
  (interestFee) => {
    if (!interestFee) return null

    return Number(BigInt(interestFee)) / 100
  },
)

export const selectDebtAddress = (vault: GetVaultData) => vault?.dToken
export const selectUnderlyingAddress = (vault: GetGenericVaultData) =>
  vault?.asset
export const selectCreatorAddress = (vault: GetVaultData) => vault?.creator
export const selectFeeReceiverAddress = (vault: GetVaultData) =>
  vault?.governorFeeReceiver

export const selectExchangeRate = createSelector(
  [selectTotalShares, selectTotalAssets],
  (totalShares, totalAssets) => {
    if (!totalShares || !totalAssets || totalShares === 0n) {
      // Default Share Token Exchange Rate is 1.00
      return "1.00"
    }

    return new Fraction(totalAssets, totalShares).toFixed(6)
  },
)

export const selectBadDebtSocialization = (vault: GetVaultData) =>
  vault?.configFlags

export const selectVaultsMap = createSelector(
  [(results: UseQueryResult<GetVaultData>[]) => results, selectQueryStatus],
  (results, status) => {
    const vaults = new Map<Address, GetVaultData>()

    if (status.isSuccess) {
      for (const result of results) {
        if (!result.data || !isAddress(result.data.vault)) continue
        vaults.set(getAddress(result.data.vault), {
          ...result.data,
          unitOfAccount:
            result.data.unitOfAccount === zeroAddress
              ? result.data.backupAssetPriceInfo.unitOfAccount
              : result.data.unitOfAccount,
        })
      }
    }

    return { ...status, data: vaults }
  },
)

export const selectTotalBorrow = createSelector(
  [
    (vaultsData: UseQueryResult<GetVaultReturnType>[]) => vaultsData,
    (_, allUnitOfAccountPrices: Map<Address, bigint>) => allUnitOfAccountPrices,
  ],
  (vaultsData, allUnitOfAccountPrices) => {
    return vaultsData
      .map((vaultData) => {
        const totalBorrowValue = selectPrice(
          vaultData.data?.totalBorrowed,
          vaultData.data,
          allUnitOfAccountPrices.get(
            vaultData.data?.unitOfAccount ?? zeroAddress,
          ) ?? 0n,
        )?.toSignificant()

        return parseFloat(totalBorrowValue ?? "0")
      })
      .reduce<number>((total, current) => total + current, 0)
  },
)

export const selectTotalSupply = createSelector(
  [
    (vaultsData: UseQueryResult<GetVaultReturnType>[]) => vaultsData,
    (_, allUnitOfAccountPrices: Map<Address, bigint>) => allUnitOfAccountPrices,
  ],
  (vaultsData, allUnitOfAccountPrices) => {
    return vaultsData
      .map((vaultData) => {
        const totalSupplyValue = selectPrice(
          vaultData.data?.totalAssets,
          vaultData.data,
          allUnitOfAccountPrices.get(
            vaultData.data?.unitOfAccount ?? zeroAddress,
          ) ?? 0n,
        )?.toSignificant()

        return parseFloat(totalSupplyValue ?? "0")
      })
      .reduce<number>((total, current) => total + current, 0)
  },
)
